/**
 * Page copy for /case-studies — the hub.
 *
 * THREE FILES WITH CLOSE NAMES. Check which one you have:
 *   - `caseStudies.ts`     — the homepage section. Holds two approved metrics.
 *   - `caseStudiesHub.ts`  — this file. The /case-studies index.
 *   - `case*.ts`           — one per detail page (caseChatbotTesting, etc).
 *
 * NO CLIENT COPY EXISTS FOR THIS PAGE. Nothing was supplied for an index, so
 * everything here is one of two things and each entry says which:
 *
 * 1. APPROVED, REUSED VERBATIM. Every card's title, summary and figures are
 *    lifted unchanged from the detail page they link to. Nothing is
 *    paraphrased, trimmed or recombined.
 *
 *    The summaries STARTED OUT as copies of those pages' own `seo.description`
 *    and are no longer identical to them: two of those descriptions ran past
 *    the ~160 characters Google shows in a result snippet and were reworded to
 *    fit. The wording here is the approved copy and is the one that shows on
 *    the page, so it stayed as it was. Do not "resync" the two — they answer
 *    different questions now, and `seo.description` is meta only, read
 *    nowhere but `src/config/seo.ts`.
 * 2. NEW COPY PENDING SIGN-OFF. The hero, the "how to read the numbers"
 *    section, the CTA and the link labels. Marked `/** NEW COPY *\/` below.
 *
 * SCOPE — three studies, not five.
 *
 * `app/case-studies/` also holds `_financial-investments` and
 * `_pharma-engagement-application`. The leading underscore is Next's private
 * folder convention: those routes do not exist, and both are blocked on
 * something only the client can supply. THEY ARE DELIBERATELY NOT LISTED HERE.
 * Adding either to this array ships a link to a 404. See CLAUDE.md, "Held out
 * of the release".
 *
 * The homepage's featured study (the 2.5x dev-environment engagement) has no
 * detail page either, so it is not listed. Its "Read the full study" link stays
 * at "#" where it is.
 */

/**
 * A figure that is NOT a client outcome — how the work runs, read straight off
 * the detail page's own Results section. `scopeLabel` is that page's wording.
 *
 * Two of the three studies have no measured figures at all. That is recorded,
 * not filled in. See the `reading` section below, which is the page explaining
 * the difference to the reader rather than hoping they don't notice.
 */
export type ScopeFigure = { value: string; label: string; note: string };

/** A measured client outcome. Only one of the three has one. */
export type Outcome = {
  beforeLabel: string;
  before: string;
  beforeNote: string;
  afterLabel: string;
  after: string;
  afterNote: string;
};

export type StudyCard = {
  badge: string;
  title: string;
  summary: string;
  href: string;
  /** Present on exactly one card, and it is the highlighted one. */
  outcome: Outcome | null;
  /** NEW COPY — pending sign-off. Only rendered when `outcome` is set. */
  outcomeLabel: string;
  figuresLabel: string;
  figures: ScopeFigure[] | null;
  appliesLabel: string;
  applies: readonly string[];
};

export const seo = {
  /** NEW COPY — pending sign-off. */
  title: "Case Studies — AI Testing & Quality Engineering",
  description:
    "Three engagements in depth: conversational AI tested in CI, on-device AI with zero data egress, and test code generated from a feature ticket.",
} as const;

export const hero = {
  /** NEW COPY — pending sign-off. */
  heading: "Case studies",
  /**
   * NOT new copy. This is the approved heading of the homepage's case-studies
   * section, verbatim from design/Homepage.dc.html — the same claim, in the
   * place the homepage section points to.
   */
  thesisLead: "Measured on production uptime, not ",
  thesisAccent: "test-case volume",
  thesisTail: ".",
  /** NEW COPY — pending sign-off. */
  body: [
    "Three engagements, written up in full: what the client was facing, what we built, and what changed. Each one is a testing problem that conventional automation does not solve — output with no single correct answer, a model that never leaves the user's phone, a framework whose conventions a generator has never seen.",
  ],
} as const;

export const studies: StudyCard[] = [
  {
    /* All five strings below are verbatim from content/caseChatbotTesting.ts. */
    badge: "Use Case #1",
    title: "End-to-End Testing of AI Chatbots at Scale",
    summary:
      "How we test conversational AI: multi-turn scenarios, LLM-as-a-judge evaluation, adversarial prompts, and regression detection on every model change.",
    href: "/case-studies/ai-chatbot-testing",
    outcome: null,
    outcomeLabel: "",
    figuresLabel: "What runs on every change",
    figures: [
      {
        value: "50+",
        label: "Research-backed metrics",
        note: "Answer relevancy, faithfulness, hallucination detection, coherence, task completion, toxicity",
      },
      {
        value: "6",
        label: "Quality dimensions measured",
        note: "Accuracy, relevance, consistency, truthfulness, safety, bias detection",
      },
      {
        value: "4",
        label: "Adversarial categories",
        note: "Prompt injection, ambiguous inputs, long conversations, invalid queries",
      },
    ],
    appliesLabel: "Where This Applies",
    applies: [
      "Customer support chatbots",
      "AI assistants and copilots",
      "RAG (Retrieval-Augmented Generation) systems",
      "Enterprise AI tools",
    ],
  },
  {
    /* Verbatim from content/caseOnDeviceAi.ts. */
    badge: "Use Case #2",
    title: "Testing On-Device AI in a Privacy-First Mobile Application",
    summary:
      "Testing AI that runs entirely on the user's device. Two-layer pipeline: mobile UI automation captures real responses, a local judge scores them. No data leaves the device.",
    href: "/case-studies/on-device-ai-testing",
    outcome: null,
    outcomeLabel: "",
    figuresLabel: "What the pipeline guarantees",
    figures: [
      {
        value: "2",
        label: "Layers, both local",
        note: "Mobile UI automation captures real responses; a local judge scores them",
      },
      {
        value: "4",
        label: "Dimensions scored on-device",
        note: "Accuracy, coherence, relevance, safety",
      },
      {
        value: "0",
        label: "Data egress",
        note: "No external service contacted; test data never leaves the device",
      },
    ],
    appliesLabel: "Where this applies",
    applies: [
      "On-device AI assistants",
      "Privacy-sensitive applications in healthcare, finance and legal",
      "Offline-first mobile AI",
      "Apps running compressed or quantised models",
    ],
  },
  {
    /*
     * Verbatim from content/caseTestGeneration.ts, including the before/after.
     *
     * "2-3 hours per feature -> under 10 minutes per feature" is a measured
     * client outcome on CLAUDE.md's approved list. NEITHER HALF MAY BE ROUNDED.
     * This is the only outcome figure on the page, which is why this card is
     * the one that inverts.
     */
    badge: "Use Case #3",
    title: "AI-Powered Test Generation for Enterprise Test Automation",
    summary:
      "An AI agent that reads your ticket, API spec, live UI and existing tests, then writes test code matching your project's conventions. 2–3 hours down to under 10 minutes.",
    href: "/case-studies/ai-test-generation",
    outcome: {
      beforeLabel: "Old Approach",
      before: "2–3 hours",
      beforeNote: "per feature",
      afterLabel: "With AI Agent",
      after: "Under 10 min",
      afterNote: "per feature",
    },
    /** NEW COPY — pending sign-off. */
    outcomeLabel: "Measured outcome",
    figuresLabel: "",
    figures: null,
    appliesLabel: "Where This Applies",
    applies: [
      "Enterprise test automation frameworks",
      "Teams maintaining tests across many services",
      "Projects with strict test patterns",
      "Organizations reducing manual QA overhead",
    ],
  },
];

/**
 * NEW COPY — pending sign-off, and the section most worth reading before
 * approving.
 *
 * The deleted five-study template's whole point was its fourth section: which
 * number to read and why, because three percentages with no interpretation is a
 * scoreboard. This is the hub's version of that. It exists because two of the
 * three studies carry scope figures and no outcomes, and a reader scanning
 * "50+", "6", "4", "2", "4", "0" will otherwise read six results that aren't.
 *
 * The detail pages already label their own gaps in public ("Currently claimed,
 * pending figures"), so this states nothing the site does not already say.
 */
export const reading = {
  heading: "How to read the numbers here",
  lead: "A figure on this page is one of two things, and the difference is the whole point.",
  items: [
    {
      label: "A measured outcome",
      body: "Something a client observed change. One study here has one: test generation went from 2–3 hours per feature to under 10 minutes.",
    },
    {
      label: "Scope",
      body: "What the work runs on every change — how many metrics, how many dimensions, how much data leaves the device. Worth knowing, but it describes the method, not a result.",
    },
  ],
  close:
    "The other two studies have no measured figures yet. Those sections say so rather than carry an estimate.",
} as const;

/** NEW COPY — pending sign-off. */
export const cta = {
  question: "Recognise your problem in one of these?",
  body: "Bring the release that keeps going wrong. We'll tell you what we'd test first and whether we're the right team for it.",
} as const;

/** NEW COPY — pending sign-off. Mirrors the approved "Read the full study". */
export const linkLabel = "Read the case study";
